import {Component, computed, inject, input, OnInit, Optional, SkipSelf} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormGroupName,
  ReactiveFormsModule,
} from '@angular/forms';
import {CommonModule, formatNumber} from '@angular/common';
import {InputNumberModule} from 'primeng/inputnumber';
import {InputTextModule} from 'primeng/inputtext';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {of, startWith, switchMap} from 'rxjs';

@Component({
  selector: 'app-form-field-number',
  imports: [CommonModule, ReactiveFormsModule, InputNumberModule, InputTextModule, TranslocoPipe],
  templateUrl: './form-field-number.component.html',
  styleUrls: ['./form-field-number.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (parentGroupName: FormGroupName, parentFormDir: FormGroupDirective) =>
        parentGroupName ?? parentFormDir,
      deps: [
        [new Optional(), new SkipSelf(), FormGroupName],
        [new Optional(), new SkipSelf(), FormGroupDirective],
      ],
    },
  ],
})
export class FormFieldNumberComponent implements OnInit {
  id = input.required<string>();
  fieldName = input.required<string>();
  label = input<string>('');
  placeholder = input<string>('');
  isReadOnly = input<boolean>(false);
  smallLabel = input<boolean>(false);
  showButtons = input<boolean>(true);
  mode = input<'decimal' | 'integer'>('decimal');
  step = input<number | undefined>(undefined);
  minFractionDigits = input<number>(0);
  maxFractionDigits = input<number>(2);
  alignRight = input<boolean>(false);
  icon = input<string | undefined>(undefined);
  max = input<number | undefined>(undefined);
  min = input<number | undefined>(undefined);
  locale = input<string>('');

  @Optional() private readonly controlContainer = inject(ControlContainer);
  private readonly translocoService = inject(TranslocoService);

  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly effectiveLocale = computed(() => this.locale() || this.activeLang());

  private extractedMin?: number;
  private extractedMax?: number;

  private static readonly MIN_SENTINEL = Number.NEGATIVE_INFINITY;
  private static readonly MAX_SENTINEL = Number.POSITIVE_INFINITY;

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  readonly formControlValue = toSignal(
    toObservable(this.fieldName).pipe(
      switchMap((name) => {
        const ctrl = this.form?.get(name);
        return ctrl ? ctrl.valueChanges.pipe(startWith(ctrl.value)) : of(null);
      }),
    ),
  );

  readonly formattedReadOnlyValue = computed(() => {
    const val = this.formControlValue();
    if (val === null || val === undefined || val === '') return '';
    if (this.mode() === 'integer') return formatNumber(val, this.effectiveLocale(), '1.0-0');
    return formatNumber(
      val,
      this.effectiveLocale(),
      `1.${this.minFractionDigits()}-${this.maxFractionDigits()}`,
    );
  });

  readonly isRequired = computed(() => {
    const control = this.form?.get(this.fieldName());
    if (!control || !control.validator) return false;
    const validator = control.validator({value: ''} as AbstractControl);
    return !!validator?.['required'];
  });

  readonly finalMin = computed(() => this.min() ?? this.extractedMin ?? 0);
  readonly finalMax = computed(() => this.max() ?? this.extractedMax);

  ngOnInit() {
    const ctrl = this.form?.get(this.fieldName());
    this.extractedMin = this.extractThreshold(ctrl, 'min', FormFieldNumberComponent.MIN_SENTINEL);
    this.extractedMax = this.extractThreshold(ctrl, 'max', FormFieldNumberComponent.MAX_SENTINEL);
  }

  private extractThreshold(
    control: AbstractControl | null,
    type: 'min' | 'max',
    sentinel: number,
  ): number | undefined {
    const validatorFn = control?.validator;
    if (!validatorFn) return undefined;
    const testCtrl = new FormControl(sentinel);
    const errors = validatorFn(testCtrl) || {};
    return (errors as any)[type]?.[type];
  }
}
